import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Card,
  Spinner,
  Alert,
  Form,
  Button,
  Row,
  Col,
  Badge,
  Table
} from 'react-bootstrap';
import SignatureCanvas from 'react-signature-canvas';
import { FaCheckCircle, FaSignature, FaBoxOpen, FaClock, FaBan } from 'react-icons/fa';
import { getPrestamoPorTokenPublico, confirmarEntregaPublicoAPI } from '../../api/common';
import capitalizeText from '../../utils/capitalizeText';

const ConfirmarEntregaPublico = () => {
  const { token } = useParams();
  const sigCanvas = useRef(null);

  const [estado, setEstado] = useState('cargando'); // cargando | activo | expirado | confirmado | anulado | error
  const [prestamo, setPrestamo] = useState(null);

  console.log('prestamo:', prestamo); // Debug: revisar datos del préstamo
  const [submitting, setSubmitting] = useState(false);
  const [submitOk, setSubmitOk] = useState(false);

  // Campos del formulario
  const [nombreRecibe, setNombreRecibe] = useState('');
  const [cargoRecibe, setCargoRecibe] = useState('');
  const [recibidoSatisfaccion, setRecibidoSatisfaccion] = useState(false);
  const [observaciones, setObservaciones] = useState('');
  const [firmaCargada, setFirmaCargada] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    cargarPrestamo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const cargarPrestamo = async () => {
    try {
      const resp = await getPrestamoPorTokenPublico(token);
      if (resp.status === 200) {
        const data = resp.data.data;
        setPrestamo(data);

        if (data._tokenExpirado) {
          setEstado('expirado');
        } else if (data.estado === 'confirmado') {
          setEstado('confirmado');
        } else if (data.estado === 'anulado') {
          setEstado('anulado');
        } else {
          setEstado('activo');
        }
      } else {
        setEstado('error');
      }
    } catch {
      setEstado('error');
    }
  };

  const limpiarFirma = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
      setFirmaCargada(false);
    }
  };

  const validar = () => {
    const errs = {};
    if (!nombreRecibe.trim()) errs.nombreRecibe = 'Campo obligatorio';
    if (!cargoRecibe.trim()) errs.cargoRecibe = 'Campo obligatorio';
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) errs.firma = 'La firma es obligatoria';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleConfirmar = async () => {
    if (!validar()) return;

    setSubmitting(true);
    try {
      const firmaDataURL = sigCanvas.current.toDataURL('image/png');

      const resp = await confirmarEntregaPublicoAPI(token, {
        nombreRecibe: nombreRecibe.trim(),
        cargoRecibe: cargoRecibe.trim(),
        recibidoSatisfaccion,
        observaciones: observaciones.trim(),
        firma: firmaDataURL
      });

      if (resp.status === 200) {
        setSubmitOk(true);
        setEstado('confirmado');
        setPrestamo(resp.data.data || prestamo);
      } else {
        setErrors({ general: resp.data?.message || 'Ocurrió un error al confirmar' });
      }
    } catch (err) {
      setErrors({ general: 'Ocurrió un error inesperado. Intenta nuevamente.' });
    } finally {
      setSubmitting(false);
    }
  };

  const getBadgeEquipo = (estadoEq) => {
    const map = {
      prestado: { bg: 'warning', label: 'Prestado' },
      devuelto: { bg: 'success', label: 'Devuelto' },
      devuelto_parcial: { bg: 'info', label: 'Dev. Parcial' }
    };
    const cfg = map[estadoEq] || { bg: 'secondary', label: estadoEq };
    return <Badge bg={cfg.bg}>{cfg.label}</Badge>;
  };

  // ─── Pantallas de estado ──────────────────────────────────────────────────

  if (estado === 'cargando') {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Cargando información del préstamo...</p>
        </div>
      </div>
    );
  }

  if (estado === 'expirado') {
    return (
      <Container className="py-5" style={{ maxWidth: 540 }}>
        <Card className="shadow text-center p-4">
          <FaClock size={48} className="text-warning mx-auto mb-3" />
          <h4>Link expirado</h4>
          <p className="text-muted">
            Este link de confirmación venció hace más de 7 días.<br />
            Solicita al laboratorio que genere un nuevo link.
          </p>
        </Card>
      </Container>
    );
  }

  if (estado === 'anulado') {
    return (
      <Container className="py-5" style={{ maxWidth: 540 }}>
        <Card className="shadow text-center p-4">
          <FaBan size={48} className="text-danger mx-auto mb-3" />
          <h4>Préstamo anulado</h4>
          <p className="text-muted">
            Este préstamo ha sido anulado y no puede confirmarse la entrega.
          </p>
        </Card>
      </Container>
    );
  }

  if (estado === 'error') {
    return (
      <Container className="py-5" style={{ maxWidth: 540 }}>
        <Card className="shadow text-center p-4">
          <FaBan size={48} className="text-secondary mx-auto mb-3" />
          <h4>Link inválido</h4>
          <p className="text-muted">
            No se encontró ningún préstamo asociado a este link.<br />
            Verifica que la URL sea correcta.
          </p>
        </Card>
      </Container>
    );
  }

  if (estado === 'confirmado') {
    const conf = prestamo?.confirmacionEntrega;
    return (
      <Container className="py-5" style={{ maxWidth: 620 }}>
        <Card className="shadow w-100">
          <Card.Header className="bg-success text-white text-center py-3 w-100">
            <FaCheckCircle size={32} className="mb-1" />
            <h5 className="mb-0">
              {submitOk ? '¡Entrega confirmada exitosamente!' : 'Entrega ya confirmada'}
            </h5>
          </Card.Header>
          <Card.Body className="w-100">
            {prestamo && (
              <>
                <p className="mb-1"><strong>Acta:</strong> {prestamo.numeroActa}</p>
                <p className="mb-1"><strong>OT:</strong> {prestamo.ot?.numeroOrden || '-'}</p>
                <h6 className="mt-3">Equipos ({prestamo.equipos?.length || 0})</h6>
                <Table size="sm" bordered>
                  <thead>
                    <tr>
                      <th>Inventario</th>
                      <th>Descripción</th>
                      <th>Serie</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(prestamo.equipos || []).map((eq, i) => (
                      <tr key={i}>
                        <td>{eq.snapshotEquipo?.inventario || eq.codigo || '-'}</td>
                        <td style={{ whiteSpace: 'normal' }}>
                          {eq.snapshotEquipo?.item || eq.descripcion || '-'}
                          <br />
                          <small className="text-muted">
                            {eq.snapshotEquipo?.marca || eq.marca || '-'} {eq.snapshotEquipo?.modelo || eq.modelo || '-'}
                          </small>
                        </td>
                        <td>{eq.snapshotEquipo?.serie || eq.serie || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
            {/** Aca se coloca la seccion de la persona que recibe, la firma y la fecha  la firma esta en base64 se debe mostrar como imagen */}
            <div className="mt-4">
              <h6>Datos de quien recibe</h6>
              {prestamo?.confirmacionEntrega?.firma ? (
                <img src={prestamo.confirmacionEntrega.firma} alt="Firma" style={{ maxWidth: '200px' }} />
              ) : (
                <p>-</p>
              )}
              <p className="mb-1"><strong>Nombre:</strong> {prestamo?.confirmacionEntrega?.nombreRecibe || '-'}</p>
              <p className="mb-1"><strong>Cargo:</strong> {capitalizeText( prestamo?.confirmacionEntrega?.cargoRecibe || '-')}</p>
              <p className="mb-1"><strong>Fecha:</strong> {capitalizeText( prestamo?.confirmacionEntrega?.fecha ? new Date(prestamo.confirmacionEntrega.fecha).toLocaleString() : '-')}</p>

            </div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // ─── Formulario activo ────────────────────────────────────────────────────

  return (
    <Container className="py-4" style={{ maxWidth: 700 }}>
      {/* Cabecera */}
      <Card className="shadow mb-4 w-100">
        <Card.Header className="text-white py-3 w-100" style={{ background: 'rgb(20, 66, 141)' }}>
          <div className="d-flex align-items-center gap-2">
            <FaBoxOpen size={24} />
            <div>
              <h5 className="mb-0">Confirmación de Entrega</h5>
              <small className="opacity-75">Acta {prestamo?.numeroActa} — OT {prestamo?.ot?.numeroOrden || '-'}</small>
            </div>
          </div>
        </Card.Header>
        <Card.Body className='w-100'>
          <p className="text-muted mb-2">
            Por favor verifica los equipos que se listan a continuación y completa el formulario para confirmar la recepción.
          </p>

          {/* Lista de equipos */}
          <h6>Equipos a recibir ({prestamo?.equipos?.length || 0})</h6>
          <Table size="sm" bordered responsive className="mb-0">
            <thead className="table-light">
              <tr>
                <th>Inventario</th>
                <th>Descripción</th>
                <th>Serie</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {(prestamo?.equipos || []).map((eq, i) => (
                <tr key={i}>
                  <td>{eq.snapshotEquipo?.inventario || eq.codigo || '-'}</td>

                  {/**Coloco el nombre del item y en segunda linea marca y modelo */}
                  <td style={{ whiteSpace: 'normal' }}>
                    {eq.snapshotEquipo?.item || eq.descripcion || '-'}
                    <br />
                    <small className="text-muted">
                      {eq.snapshotEquipo?.marca || eq.marca || '-'} {eq.snapshotEquipo?.modelo || eq.modelo || '-'}
                    </small>
                  </td>

                  <td>{eq.snapshotEquipo?.serie || eq.serie || '-'}</td>
                  <td>{getBadgeEquipo(eq.estado)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Formulario */}
      <Card className="shadow w-100">
        <Card.Header className="bg-light w-100">
          <strong>Datos de quien recibe</strong>
        </Card.Header>
        <Card.Body className='w-100'>
          {errors.general && <Alert variant="danger">{errors.general}</Alert>}

          <Row className="mb-3">
            <Col sm={6}>
              <Form.Group>
                <Form.Label>Nombre completo <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Nombre de quien recibe"
                  value={nombreRecibe}
                  onChange={(e) => setNombreRecibe(e.target.value)}
                  isInvalid={!!errors.nombreRecibe}
                />
                <Form.Control.Feedback type="invalid">{errors.nombreRecibe}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group>
                <Form.Label>Cargo <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Cargo de quien recibe"
                  value={cargoRecibe}
                  onChange={(e) => setCargoRecibe(e.target.value)}
                  isInvalid={!!errors.cargoRecibe}
                />
                <Form.Control.Feedback type="invalid">{errors.cargoRecibe}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Se reciben todos los equipos a satisfacción"
              checked={recibidoSatisfaccion}
              onChange={(e) => setRecibidoSatisfaccion(e.target.checked)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Observaciones</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Observaciones sobre la entrega (opcional)..."
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
            />
          </Form.Group>

          {/* Firma */}
          <Form.Group className="mb-4">
            <Form.Label>
              <FaSignature className="me-1" />
              Firma <span className="text-danger">*</span>
            </Form.Label>
            <div className="border rounded p-2" style={{ backgroundColor: '#f8f9fa' }}>
              <SignatureCanvas
                ref={sigCanvas}
                canvasProps={{
                  width: 600,
                  height: 180,
                  style: {
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    width: '100%',
                    height: '180px'
                  }
                }}
                onEnd={() => setFirmaCargada(true)}
              />
              {errors.firma && <div className="text-danger small mt-1">{errors.firma}</div>}
              <div className="text-end mt-1">
                <Button variant="outline-secondary" size="sm" onClick={limpiarFirma}>
                  Limpiar firma
                </Button>
              </div>
            </div>
            <Form.Text className="text-muted">Firme en el recuadro blanco con el dedo o mouse</Form.Text>
          </Form.Group>

          <div className="d-grid">
            <Button
              variant="success"
              size="lg"
              onClick={handleConfirmar}
              disabled={submitting}
            >
              {submitting
                ? <><Spinner animation="border" size="sm" className="me-2" />Confirmando...</>
                : <><FaCheckCircle className="me-2" />Confirmar Recepción de Equipos</>
              }
            </Button>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ConfirmarEntregaPublico;
